import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Shield, Plus, X, User } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// Placeholder-guard allowlist. Pre-approve Jordan vanity/VIP numbers
// so the isPlausibleMobile() rule (all-same-digit, sequential runs)
// lets them through at POS signup + Twilio send. Companion to the
// cashier-side "Force accept" flow at the till (POS UI in a future
// release); until then this page is the primary way to whitelist.

interface Entry {
  phone: string
  reason: string | null
  added_by_admin_id: string | null
  added_by_cashier_id: string | null
  created_at: string
  admin_name: string | null
  cashier_name: string | null
}

export default function PhoneAllowlist() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<{ entries: Entry[] }>({
    queryKey: ['phone-allowlist'],
    queryFn: () => api.get('/phone-allowlist').then(r => r.data),
  })

  const [phone, setPhone] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: async () => {
      setError(null)
      await api.post('/phone-allowlist', { phone: phone.trim(), reason: reason.trim() || null })
    },
    onSuccess: () => {
      setPhone('')
      setReason('')
      qc.invalidateQueries({ queryKey: ['phone-allowlist'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } } }
      setError(err.response?.data?.message ?? 'Could not add')
    },
  })

  const del = useMutation({
    mutationFn: async (p: string) => api.delete(`/phone-allowlist/${encodeURIComponent(p)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['phone-allowlist'] }),
  })

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Shield size={20} className="text-[#c41e3a]" />
          Phone allowlist
        </h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Pre-approve Jordan vanity / VIP mobile numbers so the placeholder-guard doesn&apos;t block them. Guard normally rejects all-same-digit (<code className="text-white/70">0000000</code>, <code className="text-white/70">1111111</code>&nbsp;…) and sequential runs (<code className="text-white/70">1234567</code>) at POS signup and Twilio send.
        </p>
      </div>

      {canManage && (
        <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="0778006000 or +962778006000"
              className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white md:col-span-2"
            />
            <input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason (optional) — e.g. VIP customer Robert"
              className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white"
            />
            <button
              onClick={() => add.mutate()}
              disabled={!phone.trim() || add.isPending}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded bg-[#c41e3a] hover:bg-[#a01830] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={14} />
              {add.isPending ? 'Adding…' : 'Allow'}
            </button>
          </div>
          {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
        </div>
      )}

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.entries ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Allowlist is empty. Only real vanity / VIP numbers need to go here.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Phone</th>
                <th className="text-left px-4 py-3 font-medium">Reason</th>
                <th className="text-left px-4 py-3 font-medium">Added by</th>
                <th className="text-left px-4 py-3 font-medium">When</th>
                <th className="text-right px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data!.entries.map(e => (
                <tr key={e.phone} className="border-t border-white/5">
                  <td className="px-4 py-3 text-white font-mono">{e.phone}</td>
                  <td className="px-4 py-3 text-gray-300">{e.reason ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    <div className="flex items-center gap-1.5">
                      <User size={11} />
                      {e.admin_name ? `${e.admin_name} (admin)` : e.cashier_name ? `${e.cashier_name} (cashier)` : '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs font-mono">{formatDateTime(e.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {canManage && (
                      <button
                        onClick={() => { if (confirm(`Remove ${e.phone} from the allowlist?`)) del.mutate(e.phone) }}
                        className="text-gray-500 hover:text-red-400"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
